import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, TextField } from "@material-ui/core";
import { Add, ChevronRight, Clear, ExpandMore, Remove } from "@material-ui/icons";
import Search from "@material-ui/icons/Search";
import { TreeItem, TreeView } from "@material-ui/lab";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { MockCollections } from "Collections";
import { persistedDb } from "FirebaseConfigs";
import { IdMap } from "models/IdMap";
import { IndustryTypes } from "models/IndustryTypes";
import { OfferRoom } from "models/OfferRoom";
import { OfferRoomPriceField } from "models/OfferRoomPriceField";
import { PriceField } from "models/PriceField";
import { PriceFieldCategories } from "models/PriceFieldCategories";
import { EnumPresentMap } from "models/PropertyMap";
import { Rooms } from "models/Rooms";
import { useFilterByName } from "screens/ListScreen";
import { handleEnterKey } from "Util/handleEnterKey";
import { isEmpty } from "Util/isEmpty";
import { sortAlphabetically } from "Util/sortAlphabetically";

import { roomIcons } from "./OfferRoomCreationScreen";
import { formatMoney } from "./SummaryTable";

type Props = {
    roomTag?: Rooms.Type
    priceFieldGroups: PriceFieldGroups | null,
    priceFields: IdMap<PriceField> | null
    offerRoom: OfferRoom,
    setOfferRoom(setRoom: OfferRoom | null): void
    comment: string
    name: string
    setComment: (value: string) => void
    setName: (value: string) => void
    customPriceFields: IdMap<PriceField>
    hideUnitPrices: boolean
    industries: EnumPresentMap<IndustryTypes.IndustryTypes>
}

/*

                <FormControlLabel
                    aria-label="Remove Room"
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    control={
                        <IconButton size="small" onClick={() => setOfferRoom(null)}>
                            <Delete />
                        </IconButton>
                    }
                    label={rooms[offerRoom.roomId].name}
                />

*/


export type PriceFieldGroups = {
    [roomId: string]: IdMap<PriceField>
}

type ModalProps = {
    initialValue: number
    onClose: (value: number) => void
    onCancel: () => void
    title: string
    returnNan?: boolean
    children?: React.ReactNode
    extraAction?: React.ReactNode
}

export function NumberInputModal({ extraAction, initialValue, onClose, onCancel, title, returnNan, children }: ModalProps) {
    const [value, setValue] = useState('');

    const asNumber = Number(value);

    function onComplete() {
        onClose((value === '' || isNaN(asNumber)) ? (returnNan ? (value === '' ? NaN : asNumber) : initialValue) : Math.abs(asNumber))
    }

    const textField = (
        <TextField
            type="number"
            variant="outlined"
            value={value || ''}
            onChange={e => setValue(e.target.value)}
            placeholder={String(isNaN(initialValue) ? '' : initialValue)}
            autoFocus
            fullWidth
            onKeyPress={handleEnterKey(onComplete)}

        />
    )

    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {children ? (
                    <List>
                        {children}
                        <ListItem disableGutters>
                            {textField}
                        </ListItem>
                    </List>
                ) : textField}
            </DialogContent>
            <DialogActions>
                {extraAction}
                <Button onClick={onCancel}>Avbryt</Button>
                <Button onClick={onComplete} color="primary">Ok</Button>
            </DialogActions>
        </Dialog>
    )
}

type PriceFieldItemProps = {
    setModalRoomPriceField: (roomPriceField: OfferRoomPriceField) => void
    updateRoom: (roomPriceField: OfferRoomPriceField, newCount: number) => void
    roomPriceField: OfferRoomPriceField
    offerRoom: OfferRoom
    createOnly?: boolean
    isAdded?: boolean
    dense?: boolean
    hideUnitPrices: boolean
}

function PriceFieldItem({ setModalRoomPriceField, updateRoom, roomPriceField, offerRoom, createOnly, isAdded, dense = true, hideUnitPrices }: PriceFieldItemProps) {
    const minusButton = (
        <ListItemIcon>
            <IconButton color="secondary" edge="start" onClick={(e) => {
                updateRoom(roomPriceField, roomPriceField.count - 1)
                //TODO: use an item action instead
                e.stopPropagation()
            }}>
                <Remove />
            </IconButton>
        </ListItemIcon>
    )

    const addButton = (
        <IconButton color={(createOnly && isAdded) ? "primary" : "secondary"} edge="end" onClick={() => updateRoom(roomPriceField, roomPriceField.count + 1)}>
            <Add />
        </IconButton>
    )

    const priceText = hideUnitPrices ? ' stk' : ' x ' + formatMoney(roomPriceField.priceField.price) + ' = ' + formatMoney(roomPriceField.priceField.price * roomPriceField.count)

    return (
        <ListItem
            dense={dense}
            divider
            button
            onClick={() => setModalRoomPriceField(roomPriceField)}
        >
            {minusButton}
            <ListItemText
                id={offerRoom.room.id + roomPriceField.priceField.id}
                primary={roomPriceField.priceField.name?.text}
                secondary={roomPriceField.count + priceText}
            />
            <ListItemSecondaryAction>
                {addButton}
            </ListItemSecondaryAction>
        </ListItem>
    )
}

function newRoomPriceField(priceField: PriceField) {
    return {
        id: persistedDb.collection(MockCollections.OFFER_ROOM_PRICE_FIELDS).doc().id,
        count: 0,
        priceField,
    }
}

export function OfferRoomEditor({ industries, offerRoom, setOfferRoom, comment, setComment, priceFieldGroups, customPriceFields, priceFields, hideUnitPrices, name, setName, roomTag }: Props) {
    const [modalRoomPriceField, setModalRoomPriceField] = useState<OfferRoomPriceField | null>(null);
    const roomPriceFields = Object.values(offerRoom.priceFields).sort((a, b) => sortAlphabetically(a.priceField.name?.text, b.priceField.name?.text))
    const customPriceFieldList = Object.values(customPriceFields)
        .sort((a, b) => sortAlphabetically(a.name?.text, b.name?.text))

    function updateRoom(roomPriceField: OfferRoomPriceField, pricefieldCount: number) {
        const { [roomPriceField.id]: current, ...rest } = offerRoom.priceFields;

        //Extra logic to remove pricefieldonent entirely and not leave behind an empty key in the object
        const newPriceField: IdMap<OfferRoomPriceField> = (pricefieldCount <= 0) ? {} : {
            [roomPriceField.id]: {
                ...roomPriceField,
                count: pricefieldCount,
            },
        }

        setOfferRoom({
            ...offerRoom,
            priceFields: {
                ...rest,
                ...newPriceField,
            }
        })
    }

    const [search, setSearch] = useState('')

    const filteredPriceFields = useFilterByName<PriceField>(search, useMemo(() => priceFields && Object.values(priceFields), [priceFields]), useCallback((priceField) => priceField.name?.searchStrings.text, []))

    const [expanded, setExpanded] = useState<string[]>([])
    const [manualExpanded, setManualExpanded] = useState<PriceFieldCategories.Type[]>([])

    useEffect(() => {
        setExpanded(isEmpty(search) ? [] : PriceFieldCategories.FlatEnumList)
    }, [search, setExpanded])



    const usedIndustryTypes: IndustryTypes.IndustryType[] = IndustryTypes.FlatEnumList.filter(industryType => industries[industryType])

    const categoryTree = usedIndustryTypes.map((industryType) => {
        const priceFieldGroups = PriceFieldCategories.GroupedList[industryType]?.map(category => {
            const matchingPriceFields = filteredPriceFields?.filter(priceField => !!priceField.categories.map[category.id])
            if (!matchingPriceFields?.length) {
                return null
            }
            return (
                <TreeItem key={category.id} nodeId={category.id} label={category.name}>
                    <List>
                        {matchingPriceFields.map(priceField => {
                            const roomPriceField = Object
                                .values(offerRoom.priceFields).sort((a, b) => sortAlphabetically(a.priceField.name?.text, b.priceField.name?.text))
                                .find(roomPriceField => roomPriceField.priceField.id === priceField.id)
                                || newRoomPriceField(priceField)

                            const isAdded = roomPriceFields.some(roomPriceField => roomPriceField.priceField.id === priceField.id)

                            return (
                                <PriceFieldItem
                                    createOnly
                                    isAdded={isAdded}
                                    key={'category-price-field-' + roomPriceField.id}
                                    setModalRoomPriceField={setModalRoomPriceField}
                                    updateRoom={updateRoom}
                                    roomPriceField={roomPriceField}
                                    offerRoom={offerRoom}
                                    hideUnitPrices={hideUnitPrices}

                                />
                            )
                        })}
                    </List>
                </TreeItem>
            )
        })

        if ((priceFieldGroups.every(val => val === null))) {
            return null
        }

        return (

            <div key={'industryType-' + industryType}>
                {usedIndustryTypes.length > 1 ? <ListSubheader>{IndustryTypes.Flat[industryType].name}</ListSubheader> : null}
                {priceFieldGroups}
            </div>
        )
    })
    return (
        <List>
            <ListItem>
                <TextField
                    InputProps={{
                        startAdornment: roomTag ? (
                            <InputAdornment position="start">
                                {// TODO: Why is this breaking
                                    (roomIcons[roomTag]) && React.createElement(roomIcons[roomTag] as any)}
                            </InputAdornment>
                        ) : undefined,
                    }}
                    type="text"
                    value={name}
                    fullWidth
                    onChange={e => setName(e.target.value)}
                    placeholder={offerRoom.room.name}
                    onClick={e => e.stopPropagation()}
                    style={{ marginBottom: 0 }}
                />
            </ListItem>
            {!modalRoomPriceField ? null : (
                <NumberInputModal
                    title="Hvor mange?"
                    initialValue={modalRoomPriceField?.count || 0}
                    onClose={newValue => {
                        updateRoom(modalRoomPriceField, newValue)
                        setModalRoomPriceField(null)
                    }}
                    onCancel={() => {
                        setModalRoomPriceField(null)
                    }}
                />
            )}
            <Box width="100%">
                <List>
                    <ListItem>
                        <TextField
                            type="text"
                            variant="outlined"
                            value={comment}
                            fullWidth
                            onChange={e => setComment(e.target.value)}
                            placeholder="Kommentar"
                            onClick={e => e.stopPropagation()}
                        />
                    </ListItem>
                    {roomPriceFields.map(roomPriceField => (
                        <PriceFieldItem
                            hideUnitPrices={hideUnitPrices}
                            key={'room-price-field-' + roomPriceField.id}
                            setModalRoomPriceField={setModalRoomPriceField}
                            updateRoom={updateRoom}
                            roomPriceField={roomPriceField}
                            offerRoom={offerRoom}

                        />
                    ))}
                    {customPriceFieldList.length ? (
                        <ListSubheader>Nye prisfelter</ListSubheader>
                    ) : null}
                    {customPriceFieldList.map(priceField => {
                        const roomPriceField = Object
                            .values(offerRoom.priceFields).sort((a, b) => sortAlphabetically(a.priceField.name?.text, b.priceField.name?.text))
                            .find(roomPriceField => roomPriceField.priceField.id === priceField.id)
                            || newRoomPriceField(priceField)
                        return (
                            <PriceFieldItem
                                hideUnitPrices={hideUnitPrices}
                                createOnly
                                isAdded={roomPriceFields.some(roomPriceField => roomPriceField.priceField.id === priceField.id)}
                                key={'new-price-field-' + priceField.id}
                                setModalRoomPriceField={setModalRoomPriceField}
                                updateRoom={updateRoom}
                                roomPriceField={roomPriceField}
                                offerRoom={offerRoom}

                            />
                        )
                    })}
                    <ListSubheader>Prisfelter</ListSubheader>
                    <ListItem>
                        <TextField
                            InputProps={{
                                startAdornment: roomTag ? (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ) : undefined,
                                endAdornment: roomTag ? (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setSearch('')}>
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                            type="text"
                            fullWidth
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={'Søk i prisfelter'}
                            style={{ marginBottom: 10 }}
                        />
                    </ListItem>
                    <ListItem>
                        <TreeView
                            expanded={!isEmpty(search) ? expanded : manualExpanded}
                            onNodeToggle={(e, nodeIds) => !isEmpty(search) ? setExpanded(nodeIds) : setManualExpanded(nodeIds as PriceFieldCategories.Type[])}
                            defaultCollapseIcon={<ExpandMore />}
                            defaultExpandIcon={<ChevronRight />}
                            style={{ width: '100%' }}
                        >
                            {categoryTree}
                        </TreeView>
                    </ListItem>
                    {((categoryTree.every(val => val === null)) ? (
                        <ListItem>
                            <ListItemText primary={isEmpty(search) ? 'Du har ingen prisfelter enda. Du kan lage nye prisfelter øverst på denne skjermen' : 'Ingen prisfelter som passet med søket'} />
                        </ListItem>
                    ) : null)}
                </List>
            </Box>
        </List>
    )
}
